import { useCallback, useContext, useEffect, useSyncExternalStore } from 'react';

import { RequestContext } from '@components/bid_request/RequestProvider/RequestProvider';
import {
  ANCHOR_PRICE_GENERATION_STATUS,
  anchorPriceGenerationStore,
} from '@features/suggested-price-column/store/anchor-price-generation-store';

interface AnchorPricePusherPayload {
  anchor_prices_regenerated?: boolean;
  anchor_prices_regenerating?: boolean;
}

interface PusherLikeChannel {
  bind: (event: string, handler: (payload: unknown) => void) => void;
  unbind: (event: string, handler: (payload: unknown) => void) => void;
}

/**
 * Watches the per-event Pusher channel (`request-<id>`) for anchor-price generation lifecycle
 * events emitted by the Rails `Requests::LineItemPredict` job:
 *   - `anchor_prices_regenerating: true` → status becomes GENERATING
 *   - `anchor_prices_regenerated: true`  → status becomes READY
 * State lives in a module-level store so both the sticky-header banner and every Suggested Price
 * cell renderer can subscribe without prop drilling. Consumers call `reset()` after acting on the
 * ready state (Apply / Save & Apply) to return to IDLE.
 *
 * The bind effect also resets the store on each new pusher channel identity — RequestProvider
 * subscribes a fresh `request-<id>` channel per event, so a change in identity means the buyer
 * has navigated to a different event and any state left over from the prior event is stale.
 */
export const useAnchorPriceGenerationStatus = () => {
  const { pusherChannel } = useContext(RequestContext) as { pusherChannel?: PusherLikeChannel };
  const status = useSyncExternalStore(
    anchorPriceGenerationStore.subscribe,
    anchorPriceGenerationStore.getStatus,
    anchorPriceGenerationStore.getStatus,
  );

  useEffect(() => {
    if (!pusherChannel?.bind) return undefined;

    anchorPriceGenerationStore.setStatus(ANCHOR_PRICE_GENERATION_STATUS.IDLE);

    const handleChange = (payload: unknown) => {
      const data = (payload || {}) as AnchorPricePusherPayload;
      if (data.anchor_prices_regenerating) anchorPriceGenerationStore.markGenerating();
      else if (data.anchor_prices_regenerated) anchorPriceGenerationStore.setStatus(ANCHOR_PRICE_GENERATION_STATUS.READY);
    };

    pusherChannel.bind('change', handleChange);
    return () => pusherChannel.unbind('change', handleChange);
  }, [pusherChannel]);

  const reset = useCallback(
    () => anchorPriceGenerationStore.setStatus(ANCHOR_PRICE_GENERATION_STATUS.IDLE),
    [],
  );

  return { reset, status };
};
